import { Ban, Bookmark, Plus, RotateCcw, Search, ShieldCheck, X } from 'lucide-react';
import { useId, useMemo, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import {
  JEV_PARTS_MAX,
  jevReadingMicroUsdOf,
  JEV_PART_MAX_LENGTH,
  JEV_ASK_MAX_LENGTH,
  RED_FLAG_ASKS,
  UNFAIR_ASK_BODY,
  UNFAIR_ASK_TITLES,
  hasJevFlags,
  jevCostLabel,
  splitAsk,
  unfairAskKind,
  type JevAskPart,
  type JevAskResults,
  type JevProviderKind,
  type RankingRow,
  type RunJevAskInput,
  type UnfairAskKind,
} from '@nest-recruit/shared';
import { Kbd } from '../../../components/Kbd';
import { useJevAsks } from '../api/jev-api';
import { useJevBudgetButton } from '../api/use-jev-budget-gate';
import styles from './jev.module.css';

// ADR 0050/0051 (F23.15, F23.16), as in the approved mockup: the navy Ask Jev panel above the list. Say who you want
// in plain words and press Enter; the app splits it into parts (Jev can't, and nothing is cut — ADR 0051 §3), Jev
// grades each CV's evidence for each part, and the list re-orders once every CV is read. The parts show as chips to
// move, remove or add — any change runs again. An unfair ask is refused here before anything is sent, and again on
// the server. Hot filters sit underneath.

const RECENT_MAX = 5;
const TRY_MAX = 4;
const PRACTICE_NOTE = 'No Jev key on this computer, so a stand-in matches words instead. Nothing is sent anywhere. With the key, Jev answers.';

interface AskJevPanelProps {
  readonly jobId: string;
  /** Who answers: Jev, the practice stand-in, nobody (no key in production), or undefined while that loads. */
  readonly provider: JevProviderKind | null | undefined;
  readonly rows: readonly RankingRow[];
  readonly criteriaLabels: readonly string[];
  /** The ask in the address, as the server has it — running or finished. */
  readonly current: JevAskResults | undefined;
  readonly isStarting: boolean;
  readonly startingInput: RunJevAskInput | null;
  readonly onRun: (input: RunJevAskInput) => void;
  readonly onShowUnreadable: () => void;
  readonly onSaveView: (event: MouseEvent<HTMLButtonElement>) => void;
  /** The hot filters (the keyword chips), on navy. */
  readonly hotFilters: ReactNode;
  readonly isJevPickingFilters: boolean;
}

interface AskOption {
  readonly group: 'Recent asks on this job' | 'Try asking';
  readonly text: string;
  readonly meta?: string;
}

export function AskJevPanel(props: AskJevPanelProps): ReactNode {
  const { jobId, provider, rows, current, isStarting, startingInput, onRun } = props;
  const titleId = useId();
  const inputId = useId();
  const listId = useId();
  const isConnected = provider !== null;
  const history = useJevAsks(jobId, true);
  const [draft, setDraft] = useState(current?.ask.text ?? '');
  const [shownAskId, setShownAskId] = useState(current?.ask.id);
  // React's "storing information from previous renders": a saved view or a link brings its own ask's words.
  if (current && current.ask.id !== shownAskId) {
    setShownAskId(current.ask.id);
    setDraft(current.ask.text);
  }
  const [unfair, setUnfair] = useState<UnfairAskKind | null>(null);
  /** What didn't fit, said rather than cut (ADR 0051 §3). */
  const [notice, setNotice] = useState<string | null>(null);
  const [isOpen, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const options = useMemo(() => optionsOf(draft, history.data ?? [], props.criteriaLabels), [draft, history.data, props.criteriaLabels]);

  const readable = rows.filter((row) => row.cv === 'ready').length;
  // A CV that couldn't be read and no CV at all are the same to Jev: nothing to read (ADR 0050 §4).
  const unreadable = rows.filter((row) => row.cv === 'unreadable' || row.cv === 'none').length;
  const askText = isStarting && startingInput ? startingInput.text : (current?.ask.text ?? '');
  const parts = isStarting && startingInput ? startingInput.parts : (current?.ask.parts ?? []);
  const isRunning = isStarting || current?.ask.status === 'running';
  // ADR 0070 stage 2: a run costs what reading every readable CV for each part of the ask costs; held to the job's Jev budget.
  const askParts = Math.max(1, Math.min(JEV_PARTS_MAX, draft.trim().length >= 2 ? splitAsk(draft.trim()).length : parts.length));
  const runBudget = useJevBudgetButton(jobId, jevReadingMicroUsdOf(readable, askParts), { isPriced: true });

  function run(text: string): void {
    const clean = text.trim();
    if (clean.length < 2 || runBudget.blockedNote) return;
    const kind = unfairAskKind(clean);
    setUnfair(kind);
    setOpen(false);
    if (kind) return;
    const split = splitAsk(clean);
    const tooLong = split.find((part) => part.text.length > JEV_PART_MAX_LENGTH);
    if (tooLong) {
      setNotice(`“${tooLong.text.slice(0, 60)}…” is too long for one part. Split it with a comma.`);
      return;
    }
    const left = split.slice(JEV_PARTS_MAX);
    setNotice(left.length > 0 ? `Jev checks up to ${JEV_PARTS_MAX} parts at once, so it didn’t check ${left.map((part) => `“${part.text}”`).join(', ')}. Remove a part to add it.` : null);
    if (split.length > 0) onRun({ text: clean, parts: split.slice(0, JEV_PARTS_MAX) });
  }

  function rerun(next: readonly JevAskPart[]): void {
    if (next.length === 0 || askText === '') return;
    onRun({ text: askText, parts: [...next] });
  }

  function addPart(text: string): boolean {
    const clean = text.trim();
    if (clean.length < 2 || clean.length > JEV_PART_MAX_LENGTH || parts.length >= JEV_PARTS_MAX || parts.some((part) => part.text.toLowerCase() === clean.toLowerCase())) return false;
    const kind = unfairAskKind(clean);
    setUnfair(kind);
    if (kind) return false;
    rerun([...parts, { text: clean, isMust: true }]);
    return true;
  }

  function pick(option: AskOption): void {
    setDraft(option.text);
    run(option.text);
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      if (options.length > 0) setActive((index) => (index + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length);
      return;
    }
    if (event.key === 'Escape' && isOpen) {
      event.preventDefault();
      setOpen(false);
      return;
    }
    if (event.key !== 'Enter') return;
    event.preventDefault();
    const chosen = isOpen && active >= 0 ? options[active] : undefined;
    if (chosen) pick(chosen);
    else run(draft);
  }

  const activeOption = isOpen && active >= 0 && active < options.length ? `${listId}-${active}` : undefined;

  return (
    <section className={styles.panel} aria-labelledby={titleId}>
      <div className={styles.head}>
        <span className={styles.tile} aria-hidden>
          J
        </span>
        <div className={styles.titleBlock}>
          <h2 id={titleId} className={styles.title}>
            Ask Jev
          </h2>
          <p className={styles.lede}>Say who you want in plain words. Jev reads each CV’s text and puts the best matches first.</p>
        </div>
        <p className={styles.coverage}>
          <span>
            <span className={styles.coverageNumber}>{readable.toLocaleString('en-US')}</span> of {rows.length.toLocaleString('en-US')} CVs can be read
          </span>
          {unreadable > 0 && (
            <button type="button" className={styles.linkButton} onClick={props.onShowUnreadable}>
              {unreadable.toLocaleString('en-US')} can’t — show them
            </button>
          )}
        </p>
      </div>

      <div className={styles.askRow}>
        <div className={styles.askField} data-disabled={!isConnected || undefined}>
          <Search size={18} className={styles.askIcon} aria-hidden />
          <label htmlFor={inputId} className={styles.srOnly}>
            Ask Jev
          </label>
          <input
            id={inputId}
            className={styles.askInput}
            type="text"
            role="combobox"
            aria-expanded={isOpen && options.length > 0}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={activeOption}
            data-primary-search
            autoComplete="off"
            maxLength={JEV_ASK_MAX_LENGTH}
            disabled={!isConnected}
            placeholder={isConnected ? 'e.g. has written research reports, ideally uses Excel' : 'Jev isn’t connected yet'}
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
              setUnfair(null);
              setOpen(true);
              setActive(-1);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => setOpen(false)}
            onKeyDown={onKeyDown}
          />
          {draft !== '' && (
            <button type="button" className={styles.clearButton} aria-label="Clear the ask" onClick={() => setDraft('')}>
              <X size={16} aria-hidden />
            </button>
          )}
          {isOpen && options.length > 0 && (
            <ul id={listId} role="listbox" aria-label="Asks" className={styles.options}>
              {options.map((option, index) => (
                <AskOptionItem
                  key={`${option.group}:${option.text}`}
                  id={`${listId}-${index}`}
                  option={option}
                  isFirstOfGroup={options[index - 1]?.group !== option.group}
                  isActive={index === active}
                  onPick={() => pick(option)}
                />
              ))}
            </ul>
          )}
        </div>
        <button type="button" className={styles.runButton} disabled={!isConnected || draft.trim().length < 2 || Boolean(runBudget.blockedNote)} data-blocked={runBudget.blockedNote ? '' : undefined} onClick={() => run(draft)}>
          Run{runBudget.suffix}
          <Kbd>Enter</Kbd>
        </button>
      </div>
      {runBudget.blockedNote && (
        <p className={styles.lede} role="status">
          {runBudget.blockedNote}
        </p>
      )}

      {!isConnected && <p className={styles.lede}>Once the Jev key is added to the server’s settings, this box works.</p>}
      {notice && (
        <p className={styles.lede} role="status">
          {notice}
        </p>
      )}

      {unfair && (
        <div className={styles.unfair} role="status">
          <div className={styles.unfairHead}>
            <Ban size={20} className={styles.unfairIcon} aria-hidden />
            <div>
              <p className={styles.unfairTitle}>{UNFAIR_ASK_TITLES[unfair]}</p>
              <p className={styles.unfairBody}>{UNFAIR_ASK_BODY}</p>
            </div>
          </div>
          <div className={styles.picks}>
            {RED_FLAG_ASKS.map((ask) => (
              <button
                key={ask}
                type="button"
                className={styles.pickButton}
                onClick={() => {
                  setDraft(ask);
                  run(ask);
                }}
              >
                {ask}
              </button>
            ))}
          </div>
        </div>
      )}

      {parts.length > 0 && (
        <AskChecks
          parts={parts}
          current={isStarting ? undefined : current}
          isRunning={isRunning}
          onChange={rerun}
          onAdd={addPart}
          onRunAgain={() => rerun(parts)}
          onSaveView={props.onSaveView}
          canRunWithJev={provider === 'jev'}
        />
      )}

      <div className={styles.hot}>{props.hotFilters}</div>
      <p className={styles.footnote}>
        <ShieldCheck size={16} aria-hidden />
        <span>
          Hot filters come from the job and the words common in these CVs{props.isJevPickingFilters ? ', checked by Jev' : ''}. Before Jev reads a CV, the app takes out the name, photo,
          contact details, age, gender, religion and family details, and anything the CV hides or says to an AI. Jev grades real evidence, not keywords, and only re-orders: scores and tabs stay
          as the ranking set them.
        </span>
      </p>
    </section>
  );
}

function AskOptionItem({ id, option, isFirstOfGroup, isActive, onPick }: { readonly id: string; readonly option: AskOption; readonly isFirstOfGroup: boolean; readonly isActive: boolean; readonly onPick: () => void }): ReactNode {
  return (
    <>
      {isFirstOfGroup && (
        <li role="presentation" className={styles.optionGroup}>
          {option.group}
        </li>
      )}
      <li
        id={id}
        role="option"
        aria-selected={isActive}
        className={styles.option}
        // Keeps the field focused, so the click lands before the list closes on blur.
        onMouseDown={(event) => event.preventDefault()}
        onClick={onPick}
      >
        {option.text}
        {option.meta && <span className={styles.optionMeta}>{option.meta}</span>}
      </li>
    </>
  );
}

interface AskChecksProps {
  readonly parts: readonly JevAskPart[];
  /** The finished (or running) ask; undefined while it is being started. */
  readonly current: JevAskResults | undefined;
  readonly isRunning: boolean;
  readonly onChange: (parts: readonly JevAskPart[]) => void;
  readonly onAdd: (text: string) => boolean;
  readonly onRunAgain: () => void;
  readonly onSaveView: (event: MouseEvent<HTMLButtonElement>) => void;
  /** Jev is connected now, so an ask the practice stand-in answered can be run again with Jev. */
  readonly canRunWithJev: boolean;
}

/** After Run: the parts as chips (click to move between Must show and Better if), and what Jev found. */
function AskChecks({ parts, current, isRunning, onChange, onAdd, onRunAgain, onSaveView, canRunWithJev }: AskChecksProps): ReactNode {
  const [adding, setAdding] = useState('');
  const toggle = (index: number): void => onChange(parts.map((part, at) => (at === index ? { ...part, isMust: !part.isMust } : part)));
  const remove = (index: number): void => onChange(parts.filter((_, at) => at !== index));
  const groups = [
    { label: 'Must show in the CV', isMust: true },
    { label: 'Better if', isMust: false },
  ];
  return (
    <div className={styles.checks}>
      <p className={styles.checksHead}>Jev checks each CV for</p>
      <div className={styles.groups}>
        {groups.map((group) => (
          <div key={group.label} className={styles.group} role="group" aria-label={group.label}>
            <span className={styles.eyebrow}>{group.label}</span>
            {parts.map((part, index) =>
              part.isMust === group.isMust ? (
                <span key={part.text} className={styles.part} data-must={part.isMust || undefined}>
                  <button type="button" onClick={() => toggle(index)} title={`Move to ${part.isMust ? 'Better if' : 'Must show'}`} aria-label={`${part.text}. Move to ${part.isMust ? 'Better if' : 'Must show'}`}>
                    {part.text}
                  </button>
                  {parts.length > 1 && (
                    <button type="button" className={styles.partRemove} aria-label={`Remove ${part.text}`} onClick={() => remove(index)}>
                      <X size={14} aria-hidden />
                    </button>
                  )}
                </span>
              ) : null,
            )}
            {group.isMust && parts.length < JEV_PARTS_MAX && (
              <span className={styles.addPart}>
                <Plus size={14} aria-hidden />
                <input
                  className={styles.addPartInput}
                  aria-label="Add something Jev should check"
                  placeholder="Add, then Enter"
                  maxLength={JEV_PART_MAX_LENGTH}
                  value={adding}
                  onChange={(event) => setAdding(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter') return;
                    event.preventDefault();
                    if (onAdd(adding)) setAdding('');
                  }}
                />
              </span>
            )}
          </div>
        ))}
      </div>
      <AskResult current={current} isRunning={isRunning} parts={parts} onRunAgain={onRunAgain} onSaveView={onSaveView} canRunWithJev={canRunWithJev} />
    </div>
  );
}

interface AskResultProps {
  readonly current: JevAskResults | undefined;
  readonly isRunning: boolean;
  readonly parts: readonly JevAskPart[];
  readonly onRunAgain: () => void;
  readonly onSaveView: (event: MouseEvent<HTMLButtonElement>) => void;
  readonly canRunWithJev: boolean;
}

function AskResult({ current, isRunning, parts, onRunAgain, onSaveView, canRunWithJev }: AskResultProps): ReactNode {
  const isPractice = current?.ask.provider === 'practice';
  // The label stays true to how that ask was answered; once Jev is connected, one click asks Jev instead.
  const practice = isPractice ? (
    <>
      <span className={styles.practice} title={PRACTICE_NOTE}>
        Practice mode — not Jev
      </span>
      {canRunWithJev && !isRunning && (
        <button type="button" className={styles.chipButton} onClick={onRunAgain}>
          <RotateCcw size={14} aria-hidden /> Run again with Jev
        </button>
      )}
    </>
  ) : null;
  if (isRunning || !current) {
    return (
      <div className={styles.result} aria-live="polite">
        <span className={styles.resultLead}>Jev is reading the CVs…</span>
        {current && current.ask.total > 0 && (
          <span className={styles.resultMeta}>
            {current.ask.done.toLocaleString('en-US')} of {current.ask.total.toLocaleString('en-US')}
          </span>
        )}
        {practice}
      </div>
    );
  }
  const { ask, people } = current;
  if (ask.status === 'failed') {
    return (
      <div className={styles.result} aria-live="polite">
        <span className={styles.resultLead}>{ask.problem ?? 'The ask stopped.'}</span>
        <button type="button" className={styles.chipButton} onClick={onRunAgain}>
          <RotateCcw size={14} aria-hidden /> Run again
        </button>
        {practice}
      </div>
    );
  }
  const read = people.filter((person) => person.status === 'read');
  const allMusts = read.filter((person) => person.showsAllMusts).length;
  const tricks = people.filter((person) => person.flags && (person.flags.hiddenText !== null || person.flags.aiLines.length > 0 || person.flags.copiedLines > 0)).length;
  const flagged = people.filter((person) => hasJevFlags(person.flags)).length;
  const hasMusts = parts.some((part) => part.isMust);
  const notRead = ask.unreadable + ask.failed;
  const seconds = ask.finishedAt ? Math.max(1, Math.round((Date.parse(ask.finishedAt) - Date.parse(ask.askedAt)) / 1000)) : null;
  return (
    <div className={styles.result} aria-live="polite">
      <span className={styles.resultLead}>
        {hasMusts
          ? `${allMusts.toLocaleString('en-US')} ${allMusts === 1 ? 'CV meets' : 'CVs meet'} every must-have — shown, not just listed. Jev put them first, strongest evidence first.`
          : 'Jev put everyone in order of how strong their evidence is.'}
      </span>
      <span className={styles.resultMeta}>
        Read {read.length.toLocaleString('en-US')} CVs{seconds !== null ? ` in ${seconds.toLocaleString('en-US')} s` : ''}
        {isPractice ? '' : ` · cost ${jevCostLabel(ask.inputTokens, ask.reasonInputTokens, ask.reasonOutputTokens)}`}
        {tricks > 0 ? ` · ${tricks.toLocaleString('en-US')} tried to steer the ranking (flagged in red)` : flagged > 0 ? ` · ${flagged.toLocaleString('en-US')} flagged for a closer look` : ''}
        {notRead > 0 ? ` · ${notRead.toLocaleString('en-US')} not read, at the end of the list — check them by hand` : ''}
      </span>
      {practice}
      <span className={styles.resultSpacer} />
      <button type="button" className={styles.chipButton} onClick={onSaveView}>
        <Bookmark size={14} aria-hidden /> Save as view
      </button>
    </div>
  );
}

/** Recent asks on the job, then the job's criteria as asks to try — each narrowed to what has been typed. */
function optionsOf(draft: string, history: readonly { readonly text: string; readonly askedByName: string | null }[], criteriaLabels: readonly string[]): readonly AskOption[] {
  const needle = draft.trim().toLowerCase();
  const matches = (text: string): boolean => needle === '' || (text.toLowerCase().includes(needle) && text.toLowerCase() !== needle);
  const seen = new Set<string>();
  const recent: AskOption[] = [];
  for (const ask of history) {
    const key = ask.text.toLowerCase();
    if (seen.has(key) || !matches(ask.text)) continue;
    seen.add(key);
    recent.push({ group: 'Recent asks on this job', text: ask.text, meta: ask.askedByName ?? undefined });
    if (recent.length === RECENT_MAX) break;
  }
  const tries = criteriaLabels
    .filter((label) => !seen.has(label.toLowerCase()) && matches(label))
    .slice(0, TRY_MAX)
    .map((text): AskOption => ({ group: 'Try asking', text }));
  return [...recent, ...tries];
}
